import { Box, Flex, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { FaInbox } from "react-icons/fa";
import { CrmTask, TaskStatus } from "../../../models/Task";
import { crm, Pill } from "./crmTheme";
import TaskCard from "./TaskCard";

export interface TaskColumn {
  status: TaskStatus;
  tasks: CrmTask[];
}

const Column = ({
  column,
  onDragStartCard,
  onDrop,
  onTaskClick,
  onTaskDelete,
}: {
  column: TaskColumn;
  onDragStartCard: (e: React.DragEvent, t: CrmTask) => void;
  onDrop: (e: React.DragEvent, column: TaskColumn) => void;
  onTaskClick: (t: CrmTask) => void;
  onTaskDelete: (t: CrmTask) => void;
}) => {
  const [isOver, setIsOver] = useState(false);
  const accentColor = column.status.color || crm.brand;

  return (
    <Flex
      flexDir="column"
      minW="300px"
      maxW="300px"
      h="100%"
      bg={isOver ? crm.brandSoft : crm.columnBg}
      borderRadius="14px"
      border={isOver ? `2px dashed ${crm.brand}` : "2px solid transparent"}
      transition="background-color .12s ease, border .12s ease"
      overflow="hidden"
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        setIsOver(false);
        onDrop(e, column);
      }}
    >
      {/* Top accent bar */}
      <Box h="3px" bg={accentColor} borderRadius="14px 14px 0 0" />

      {/* Column header */}
      <Flex
        align="center"
        justify="space-between"
        px="14px"
        py="10px"
      >
        <Flex align="center" gap="8px">
          <Box
            w="8px"
            h="8px"
            borderRadius="full"
            bg={accentColor}
            flexShrink={0}
          />
          <Text fontWeight="700" fontSize="sm" color={crm.text}>
            {column.status.name}
          </Text>
        </Flex>
        <Pill bg={crm.surface} color={crm.textMuted}>
          {column.tasks.length}
        </Pill>
      </Flex>

      {/* Cards area */}
      <Flex flexDir="column" gap="12px" p="12px" overflowY="auto" flex="1" minH="120px">
        {column.tasks.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            onDragStart={onDragStartCard}
            onClick={onTaskClick}
            onDelete={onTaskDelete}
          />
        ))}
        {column.tasks.length === 0 && (
          <Flex
            flex="1"
            flexDir="column"
            align="center"
            justify="center"
            color={crm.textFaint}
            fontSize="sm"
            gap="8px"
            minH="100px"
            opacity={0.7}
          >
            <FaInbox size={20} />
            <Text>Arraste tasks para cá</Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const TaskBoard = ({
  columns,
  onMove,
  onTaskClick,
  onTaskDelete,
}: {
  columns: TaskColumn[];
  onMove: (task: CrmTask, target: TaskColumn) => void;
  onTaskClick: (t: CrmTask) => void;
  onTaskDelete: (t: CrmTask) => void;
}) => {
  const dragged = useRef<CrmTask | null>(null);

  const handleDragStart = (e: React.DragEvent, task: CrmTask) => {
    dragged.current = task;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id ?? "");
  };

  const handleDrop = (_e: React.DragEvent, column: TaskColumn) => {
    const task = dragged.current;
    dragged.current = null;
    if (!task) return;
    if (task.taskStatusId === column.status.id) return;
    onMove(task, column);
  };

  if (columns.length === 0) {
    return (
      <Flex w="100%" justify="center" align="center" minH="300px">
        <Text color={crm.textMuted}>
          Nenhuma coluna criada. Clique em "Gerenciar status" para criar os
          status das tasks.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex gap="16px" overflowX="auto" pb="16px" h="calc(100vh - 230px)" align="stretch">
      {columns.map((column) => (
        <Column
          key={column.status.id ?? "no-status"}
          column={column}
          onDragStartCard={handleDragStart}
          onDrop={handleDrop}
          onTaskClick={onTaskClick}
          onTaskDelete={onTaskDelete}
        />
      ))}
    </Flex>
  );
};

export default TaskBoard;
